/**
 * @fileoverview One-shot "your password changed, sign in again" flag.
 * @description Carries the post-change notice from useChangePassword to the login card through
 * sessionStorage rather than router state. A successful change signs the user out, and that flips
 * `isAuthenticated` false, which makes whichever guard wraps the current page (ProtectedRoute,
 * ForcePasswordChangeRoute) re-render and redirect to /login on its own, with no state. That
 * redirect can land after useChangePassword's own `navigate('/login', { state })` and replace it,
 * so the notice showed only some of the time. A storage flag works whichever navigation wins. The
 * same mechanism carries the "account activated, sign in" notice from useVerifyActivationCode.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

const PASSWORD_CHANGED_NOTICE_KEY = 'brownbox.auth.passwordChangedNotice'
const ACCOUNT_ACTIVATED_NOTICE_KEY = 'brownbox.auth.accountActivatedNotice'

/**
 * @description Sets a one-shot flag, ignoring unavailable storage — a notice is a nicety.
 * @param {string} key - The storage key.
 */
function markNotice(key: string): void {
  try {
    sessionStorage.setItem(key, '1')
  } catch {
    // Storage unavailable (private mode, blocked site data).
  }
}

/**
 * @description Reads a one-shot flag without clearing it.
 * @param {string} key - The storage key.
 * @returns {boolean} True if the flag is set.
 */
function hasNotice(key: string): boolean {
  try {
    return sessionStorage.getItem(key) === '1'
  } catch {
    return false
  }
}

/**
 * @description Records that the next login screen should show the password-changed notice.
 */
export function markPasswordChangedNotice(): void {
  markNotice(PASSWORD_CHANGED_NOTICE_KEY)
}

/**
 * @description Whether the password-changed notice is pending. Doesn't clear it — call
 * clearPasswordChangedNotice once it has been shown.
 * @returns {boolean} True if a password change just signed the user out.
 */
export function hasPasswordChangedNotice(): boolean {
  return hasNotice(PASSWORD_CHANGED_NOTICE_KEY)
}

/**
 * @description Records that the next login screen should show the account-activated notice.
 */
export function markAccountActivatedNotice(): void {
  markNotice(ACCOUNT_ACTIVATED_NOTICE_KEY)
}

/**
 * @description Whether the account-activated notice is pending. Doesn't clear it.
 * @returns {boolean} True if an account was just activated.
 */
export function hasAccountActivatedNotice(): boolean {
  return hasNotice(ACCOUNT_ACTIVATED_NOTICE_KEY)
}

/**
 * @description Clears every pending login notice (password changed, account activated) so each
 * shows only once.
 */
export function clearPasswordChangedNotice(): void {
  try {
    sessionStorage.removeItem(PASSWORD_CHANGED_NOTICE_KEY)
    sessionStorage.removeItem(ACCOUNT_ACTIVATED_NOTICE_KEY)
  } catch {
    // Nothing to clear if storage is unavailable.
  }
}
